import * as React from "react";
import { connect } from "react-redux";

import { apiLoginTerminal, dismissMessage, settingsView } from "tp/shared/timeclock/actions";
import { deviceConnect } from "../actions/device";
import { Clock, MessageOverlay, ConfirmMultiStamp, CostDivisionList, DeviationList, SelectDirection } from "../components";
import { Screensaver } from "../reducers/screensaver";
import { Session, View } from "tp/shared/timeclock/reducers/session";
import { Enrollment, Entry, SelfRegistration, Settings, StampForm } from "./";

import { FormattedMessage } from "react-intl";
import { LocalizationProvider } from "tp/shared/common/components/LocalizationProvider";
import { Dialog, DialogContent } from "@mui/material";
import { SaldoBalance } from "./SaldoBalance";
import BootScreen from "../components/BootScreen";
import { Messages } from "../messages";
import { screensaverPoll, screensaverReset } from "../actions";
import { Dispatch } from "redux";

const localizedMessages = {
    sv: {
        ...require("../intl/sv.json"),
        ...require("tp/views/common/intl/sv.json"),
        ...require("tp/shared/timeclock/intl/sv.json"),
        ...require("tp/shared/common/intl/sv.json"),
    },
    en: {
        ...require("../intl/en.json"),
        ...require("tp/views/common/intl/en.json"),
        ...require("tp/shared/timeclock/intl/en.json"),
    }
};

const MainScreenBody = ({ view }: { view: number }) => {
    switch (view) {
        case View.Entry:
            return <Entry />;
        case View.Stamp:
            return <StampForm
                confirmMultiStampComponent={ConfirmMultiStamp}
                costDivisionListComponent={CostDivisionList}
                deviationListComponent={DeviationList}
                enrollmentComponent={Enrollment}
                selectDirectionComponent={SelectDirection}
                onStamp={undefined}
            />;
        case View.Enrollment:
            return <Enrollment />;
        case View.SelfRegistration:
            return <SelfRegistration />;
        case View.Settings:
            return <Settings />;
        case View.Balance:
            return <SaldoBalance />;
        default:
            return <div>Unknown view</div>;
    }
};

function mapStateToProps(state): MainScreenProps {
    return {
        session: state.session,
        screensaver: state.screensaver,
        iso639LanguageCode: state.config.iso639LanguageCode
    };
}

const connector = connect(mapStateToProps);

interface MainScreenProps {
    session?: Session;
    screensaver?: Screensaver;
    iso639LanguageCode?: string;
    dispatch?: Dispatch<any, any>;
}

class _MainScreen extends React.Component<MainScreenProps, unknown> {
    async componentDidMount() {
        const { dispatch } = this.props;

        document.body.classList.add("background-map");

        try {
            await dispatch(deviceConnect());
        } catch (e) {
            console.log(e);
        }

        await dispatch(apiLoginTerminal());

        setInterval(() => {
            dispatch(screensaverPoll());
        }, 10000);

        ["mousemove", "touchstart", "touchmove", "touchend"].forEach((event) => {
            document.addEventListener(event, () => dispatch(screensaverReset()));
        });

        document.addEventListener("contextmenu", this.onContextMenu);
    }

    componentWillUnmount(): void {
        document.body.classList.remove("background-map");
    }

    onContextMenu = (event): void => {
        if (this.props.session.view !== View.Settings) {
            event.preventDefault();
        }
    };

    render() {
        const inner = this.renderInner();
        return (
            <LocalizationProvider
                locale={this.props.iso639LanguageCode}
                localizedMessages={localizedMessages}
                root={true}
            >
                {inner}
            </LocalizationProvider>
        );
    }

    renderInner(): React.ReactElement {
        const { session, screensaver } = this.props;
        const { view, messages } = session;

        if (window.navigator.userAgent.indexOf("MSIE") > 0 || window.navigator.userAgent.indexOf("Trident/") > 0) {
            return <div className="tc-message-box support"><FormattedMessage {...Messages.NoSupport} /></div>;
        } else {
            if (screensaver && screensaver.active) {
                return (
                    <div id="screensaver">
                        <Clock />
                    </div>
                );
            } else if (!view) {
                const isMessage = messages.length > 0;

                if (isMessage && messages[0].systemMessage.type == "AccessDenied") {
                    return (
                        <Dialog
                            className="tc-access-denied-box"
                            open={true}
                            fullWidth={true}
                            maxWidth="md"
                        >
                            <DialogContent>
                                <h1>
                                    <FormattedMessage {...Messages.AccessDenied} values={{ ip: messages[0].systemMessage.values.values }} />
                                </h1>
                            </DialogContent>
                        </Dialog>
                    );
                } else {
                    return <BootScreen retrying={isMessage} viewSettings={this.viewSettings} />;
                }
            } else if (view) {
                if (messages.length > 0 && typeof messages[0] != "undefined") {
                    const isUserMessage = messages[0].userMessage || messages[0].userMessageHeader;
                    if (!isUserMessage && messages[0].systemMessage && messages[0].systemMessage.type == "AccessDenied") {
                        location.reload();
                        return null;
                    }
                }
            }

            return (
                <div className={`mainScreenContainer ${View[view].toLowerCase()}x`}>
                    <Clock />
                    <MessageOverlay session={session} dismiss={this.onClearMessage} />
                    <MainScreenBody view={view} />
                </div>
            );
        }
    }

    viewSettings = () => this.props.dispatch(settingsView());

    onClearMessage = () => this.props.dispatch(dismissMessage());
}

export const MainScreen = connector(_MainScreen);
